// External Imports
import { useState, useEffect, useContext, useCallback, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { FaCarCrash, FaFileSignature } from "react-icons/fa";
import { TiCancel } from "react-icons/ti";
import { TbRepeat, TbRepeatOff } from "react-icons/tb";
import {
  MdImageSearch,
  MdOutlineLockReset,
  MdPause,
  MdPlayArrow,
  MdRefresh,
} from "react-icons/md";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import { isoDateToString } from "../../util/format-date";
import DetailPair from "../UI/DetailPair";
import Heading from "../UI/Heading";
import ChangeEndDate from "./ChangeEndDate";
import Notes from "../UI/Notes";
import BookingImages from "./BookingImages";
import BookingMetrics from "./BookingMetrics";
import BookingPayments from "./BookingPayments";
import BookingExtensions from "./BookingExtensions";
import PersonCard from "../UI/PersonCard";
import ImageSelector from "../UI/ImageSelector";
import InvoiceModal from "../payments-page/InvoiceModal";
import StartBooking from "./StartBooking";
import EndBooking from "./EndBooking";
import ForceAgreement from "./ForceAgreement";
import TagSelector from "../UI/TagSelector";
import AuditLogs from "../UI/AuditLogs";

/** ========================================================================
 * * Booking Information Page
 * Renders a page, which displays all information about a booking.
 * ========================================================================== */

export default function BookingInfo() {
  // Load Context
  const { navigate, setConfirm } = useContext(UtilityContext);

  // Get Booking ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [booking, setBooking] = useState({});
  const [loading, setLoading] = useState(true);
  const [extend, setExtend] = useState(false);
  const [show, setShow] = useState(false);
  const [sendInvoice, setSendInvoice] = useState(false);
  const [agreement, setAgreement] = useState(false);

  /* ======================= FETCH BOOKING DATA ======================= */
  const fetchBooking = useCallback(async () => {
    try {
      setLoading(true);
      setExtend(false);
      const response = await requestAdminAPI("fetch", {
        table: "booking",
        id,
        columns: [
          "vehicle { id, monthlyMiles, make { name }, plan { order }, rate, model, year, license, vin, licenseState, trackerId, cornerImage }",
          "customer { id, firstName, lastName, phone, email, photo, signature, plan: customerPlan_on_customer { plan { order } } }",
          "payments: payments_on_booking { id, amount, datePaid, status dateCreated dueDate description paidVia stripeId  }",
          "extensions: extensions_on_booking {id, endDate, createdBy {name}, dateCreated, totalFees, taxes, milesIncluded }",
          "endedBy { name photo }",
          "createdBy { name photo }",
        ],
      });
      setBooking(response);
      setLoading(false);
    } catch (err) {
      console.error(err);

      setLoading(false);
    }
  }, [id]);

  /* ======================= CANCEL BOOKING ======================= */
  const cancelBookingHandler = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("cancel-booking", { bookingId: id });
      fetchBooking();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= GENERATE INSURANCE ======================= */
  const generateInsuranceHandler = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("generate-insurance", {
        bookingId: id,
      });
      window.open(response, "_blank");
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Fetch booking data on load
  useEffect(() => {
    fetchBooking();
  }, []);

  /* ======================= RENDER ICONS ======================= 
   Render Icons based on Booking Status */
  const renderIcons = () => {
    const active = booking?.status === "active";
    const confirmed = booking?.status === "confirmed";
    if (loading)
      return <div className="w-full h-8 btn-secondary animate-pulse"></div>;
    return (
      <div className="flex items-center justify-around space-x-4">
        {confirmed && (
          <Fragment>
            <TiCancel
              onClick={() =>
                setConfirm({
                  message: "This action is not reversible.",
                  action: cancelBookingHandler,
                })
              }
              className="text-3xl icon"
              title="Cancel Booking"
            />
            <MdPlayArrow
              onClick={() => setShow("start")}
              className="text-3xl icon"
              title="Start Booking"
            />
          </Fragment>
        )}
        {active && (
          <MdPause
            onClick={() => setShow("end")}
            className="text-3xl icon"
            title="Complete Booking"
          />
        )}
        <FaCarCrash
          onClick={(e) => navigate(`/claims/add?bookingId=${booking.id}`, e)}
          className="icon"
          title="Add Insurance Claim"
        />
        <MdImageSearch
          onClick={() => setShow("images")}
          className="icon"
          title="View Images"
        />
        {booking.agreement && (
          <FaFileSignature
            onClick={() => window.open(booking.agreement, "_blank")}
            className="text-xl icon"
            title="Download Agreement"
          />
        )}
      </div>
    );
  };

  if (!id || (!loading && (!booking || !booking?.id)))
    return (
      <div className="container">
        <p className="text-stone-400">
          Booking ID Not Provided or Booking Not Found!
        </p>
      </div>
    );

  return (
    <div className="divider">
      <InvoiceModal
        open={sendInvoice ? true : false}
        setOpen={setSendInvoice}
        bookingId={booking.id}
        customerId={booking.customer?.id}
        amount={sendInvoice?.amount}
        description={sendInvoice?.description}
        category={sendInvoice?.category || "booking"}
      />
      <ChangeEndDate
        booking={booking}
        open={extend}
        setOpen={setExtend}
        onComplete={fetchBooking}
      />
      <ForceAgreement
        open={agreement}
        setOpen={setAgreement}
        booking={booking}
        onComplete={fetchBooking}
      />
      <div className="container gap-10 lg:col-span-3">
        {show === "images" ? (
          <BookingImages booking={booking} setOpen={setShow} />
        ) : show === "start" ? (
          <StartBooking
            setOpen={setShow}
            booking={booking}
            onComplete={fetchBooking}
          />
        ) : show === "end" ? (
          <EndBooking
            setOpen={setShow}
            booking={booking}
            onComplete={fetchBooking}
          />
        ) : (
          <Fragment>
            <Heading title="Booking Information" id={booking.id}>
              <AuditLogs table="booking" id={id} />
              <MdRefresh className="icon" onClick={fetchBooking} />
            </Heading>
            {bookingInfo(booking, loading)}
            <BookingMetrics booking={booking} setInvoice={setSendInvoice} />
            <Notes table="booking" id={booking.id} />
            <BookingPayments payments={booking.payments} />
            <BookingExtensions extensions={booking.extensions} />
          </Fragment>
        )}
      </div>
      <div className="sidebar">
        <div className="grid gap-8 lg:max-h-[60vh] overflow-y-scroll no-scrollbar">
          {renderIcons()}
          <PersonCard
            label="Customer:"
            name={`${booking.customer?.firstName} ${booking.customer?.lastName}`}
            secondary={booking.customer?.phone}
            image={booking.customer?.photo}
            link={`/customers/info?id=${booking.customer?.id}`}
            loading={loading}
          />
          <div
            onClick={(e) =>
              navigate(`/vehicles/info?id=${booking.vehicle?.id}`, e)
            }
            className="grid gap-2 clickable"
          >
            <ImageSelector
              image={booking.vehicle?.cornerImage}
              loading={loading}
              classes="w-full min-h-[150px]"
            />
          </div>
          {booking?.createdBy && (
            <PersonCard
              label="Created By:"
              name={`${booking.createdBy?.name}`}
              secondary={isoDateToString(booking.dateCreated)}
              image={booking.createdBy?.photo}
              link={`/employees/info?id=${booking.createdById}`}
              loading={loading}
            />
          )}
          {booking?.endedBy && (
            <PersonCard
              label="Checked Out By:"
              name={`${booking.endedBy?.name}`}
              image={booking.endedBy?.photo}
              link={`/employees/info?id=${booking.endedById}`}
              loading={loading}
            />
          )}
          <TagSelector table="booking" id={booking.id} />
        </div>
        {loading ? (
          <div className="grid gap-2">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="h-9 btn-secondary animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid gap-2">
            {booking.status === "active" && (
              <div
                onClick={() =>
                  setConfirm({
                    message:
                      "If the customer has insurance on file, you should send them a copy of their insurance instead. Generate and send this insurance card otherwise.",
                    action: generateInsuranceHandler,
                  })
                }
                className="btn-secondary"
              >
                Generate Insurance
              </div>
            )}
            <div onClick={() => setSendInvoice(true)} className="btn-secondary">
              Send Invoice
            </div>
            {booking.status !== "completed" &&
              booking.status !== "cancelled" && (
                <div onClick={() => setExtend(true)} className="btn-secondary">
                  Change End Date
                </div>
              )}
            <div onClick={() => setAgreement(true)} className="btn-secondary">
              <p>Force Agreement</p>
            </div>
            <div
              onClick={(e) =>
                navigate(
                  `/tolls?query=${booking.id}&sd=${booking.startDate}&ed=${booking.endDate}`,
                  e,
                )
              }
              className="btn-secondary"
            >
              View Tolls
            </div>
            {booking.vehicle?.make?.name === "Tesla" && (
              <div
                onClick={(e) =>
                  navigate(`/charging/booking?bookingId=${booking.id}`, e)
                }
                className="btn-secondary"
              >
                View Charging
              </div>
            )}
            <div
              onClick={() =>
                navigate(`/repossessions/add?bookingId=${booking.id}`)
              }
              className="btn-warning"
            >
              Initiate Repossession
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================== BOOKING INFO ===============================
const bookingInfo = (booking, loading) => {
  if (booking)
    return (
      <div className="info">
        <DetailPair
          label="Date Created"
          value={isoDateToString(
            booking.dateCreated,
            "eee dd MMM, yyyy - hh:mm a",
          )}
          loading={loading}
        />
        <DetailPair
          label="Start Date"
          value={isoDateToString(
            booking.startDate,
            "eee dd MMM, yyyy - hh:mm a",
          )}
          loading={loading}
        />
        <DetailPair
          label="End Date"
          value={isoDateToString(booking.endDate, "eee dd MMM, yyyy - hh:mm a")}
          loading={loading}
        />
        <DetailPair
          label="Status"
          classes={
            booking.status == "active"
              ? "text-green-600 font-bold text-lg"
              : booking.status === "confirmed"
                ? "text-yellow-600 font-bold text-lg"
                : booking.status === "completed"
                  ? "text-stone-400 font-bold text-lg"
                  : "text-blue-600 font-bold text-lg"
          }
          value={booking.status?.toUpperCase()}
          loading={loading}
        />
        <DetailPair
          label="Pick Up Location"
          value={booking.pickUpLocation}
          loading={loading}
        />
        <DetailPair
          label="Drop Off Location"
          value={booking.dropOffLocation}
          loading={loading}
        />
        <DetailPair
          label="Start/End Fuel (%)"
          value={`${booking.startFuel || ""} - ${booking.endFuel || ""}`}
          loading={loading}
        />
        <DetailPair
          label="Start/End Mileage (mi)"
          value={`${booking.startMileage || ""} - ${booking.endMileage || ""}`}
          loading={loading}
        />
        <DetailPair
          label="Miles Included/Rate"
          value={`${booking.milesIncluded || "0"}mi - $${
            booking.mileageRate
          }/mi`}
          loading={loading}
        />
        <DetailPair
          label="Delivery Fee"
          value={`$${booking?.deliveryFees || "-"}`}
          loading={loading}
        />
        <DetailPair
          label="Booking Total"
          value={`$${booking?.bookingFees || "-"}`}
          loading={loading}
        />
        <DetailPair
          label="Total (incl. taxes)"
          value={"$" + booking?.totalFees || "-"}
          loading={loading}
        />
      </div>
    );
};
